import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, ImagePlus, Loader2, X, Flag, SearchCheck, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl, uploadImage } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export const ConfirmAction = ({ trigger, title, description, confirmLabel = "Confirm", destructive, onConfirm, testid, onOpenChange }) => (
  <AlertDialog onOpenChange={onOpenChange}>
    <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
    <AlertDialogContent data-testid={`${testid}-dialog`}>
      <AlertDialogHeader><AlertDialogTitle className="font-serif text-2xl font-medium">{title}</AlertDialogTitle><AlertDialogDescription>{description}</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-full" data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className={`rounded-full ${destructive ? "bg-rose-600 hover:bg-rose-700" : ""}`}>{confirmLabel}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Avatar = ({ user, size = 40, className = "" }) => {
  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return user?.avatar ? (
    <img src={imgUrl(user.avatar)} alt={user.name} style={{ width: size, height: size }} className={`rounded-full object-cover ring-1 ring-black/5 ${className}`} />
  ) : (
    <span style={{ width: size, height: size, fontSize: size * 0.38 }} className={`inline-flex items-center justify-center rounded-full bg-[#EFE9E1] font-semibold text-slate-700 ${className}`}>{initials}</span>
  );
};

const FACT_STYLES = {
  VERIFIED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  CORROBORATED: "bg-teal-50 text-teal-800 border-teal-200",
  "SINGLE APPROVED SOURCE": "bg-sky-50 text-sky-800 border-sky-200",
  "ONE SIDE ONLY": "bg-orange-50 text-orange-800 border-orange-200",
  DISPUTED: "bg-amber-50 text-amber-800 border-amber-300",
  "INSUFFICIENT EVIDENCE": "bg-stone-100 text-stone-700 border-stone-300",
  "OPINION / NOT A FACTUAL CLAIM": "bg-violet-50 text-violet-800 border-violet-200",
  REJECTED: "bg-rose-50 text-rose-800 border-rose-200",
  SUPPORTED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  CONTRADICTED: "bg-rose-50 text-rose-800 border-rose-200",
  MIXED: "bg-amber-50 text-amber-800 border-amber-300",
};
export const FactStatus = ({ status }) => (
  <span data-testid="fact-status-badge" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${FACT_STYLES[status] || FACT_STYLES["INSUFFICIENT EVIDENCE"]}`}>{status}</span>
);


export const EnvTag = ({ env }) => {
  const map = { social: ["Feed", "--social"], facts: ["Facts", "--facts"], admin: ["Admin", "--foreground"] };
  const [label, v] = map[env];
  return (
    <span className="env-tag" style={{ color: `hsl(var(${v}))` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: `hsl(var(${v}))` }} />{label}
    </span>
  );
};

export const PageHeader = ({ env, title, subtitle, action }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 fade-up">
    <div className="space-y-1.5">
      {env && <EnvTag env={env} />}
      <h1 className="text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
      {subtitle && <p className="max-w-xl text-sm text-slate-500 sm:text-base">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Empty = ({ title, text, children, testid = "empty-state" }) => (
  <div data-testid={testid} className="surface flex flex-col items-center px-6 py-14 text-center">
    <p className="font-serif text-xl text-slate-800">{title}</p>
    {text && <p className="mt-2 max-w-md text-sm text-slate-500">{text}</p>}
    {children && <div className="mt-5">{children}</div>}
  </div>
);

export const Placeholder = ({ children }) => (
  <span className="inline-flex items-center rounded-md border border-dashed border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">MVP placeholder · {children}</span>
);

export const ImageUploader = ({ value, onChange, max = 10, testid = "image-uploader" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, max - value.length);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      onChange([...value, ...urls]);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap gap-2">
      {value.map((u, i) => (
        <div key={u} className="relative h-20 w-20 overflow-hidden rounded-xl border">
          <img src={imgUrl(u)} alt="" className="h-full w-full object-cover" />
          <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(value.filter((x) => x !== u))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X size={12} /></button>
        </div>
      ))}
      {value.length < max && (
        <button type="button" data-testid={`${testid}-add`} onClick={() => ref.current.click()} className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 text-xs text-slate-500 transition-colors hover:border-slate-500 hover:text-slate-700">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <ImagePlus size={18} />}{busy ? "Uploading" : "Add photo"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" multiple={max > 1} hidden onChange={pick} data-testid={`${testid}-input`} />
    </div>
  );
};

const privateStats = (user, me) => {
  if (!user || !me) return null;
  if (user.user_id === me.user_id) return { c: me.contributions, own: true };
  return user.contributions && me.role === "admin" ? { c: user.contributions, own: false } : null;
};

export const FactCheckerBadge = ({ user, testid = "fact-checker-badge" }) => {
  const { me } = useMe();
  const p = privateStats(user, me);
  const c = p?.c;
  if (!c || (!c.facts_verified && !c.corrections_verified)) return null;
  return (
    <span title={p.own ? "Only you and Middle's admins can see this: your Facts corroborated by approved sources, and your corrections Middle confirmed" : "Admin view (hidden from other members): this member's Facts corroborated by approved sources, and corrections Middle confirmed"} data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
      <SearchCheck size={12} aria-hidden />{c.facts_verified > 0 && <span data-testid={`${testid}-facts`}>{c.facts_verified} verified</span>}
      {c.corrections_verified > 0 && <span className={c.facts_verified > 0 ? "border-l border-emerald-200 pl-1" : ""} data-testid={`${testid}-corrections`}>{c.corrections_verified} corrected</span>}</span>
  );
};

export const EarlyConfirmerBadge = ({ user, testid = "early-confirmer-badge" }) => {
  const { me } = useMe();
  const p = privateStats(user, me);
  const n = p?.c?.early_confirmations;
  if (!n) return null;
  return (
    <span title={`${p.own ? "Only you and Middle's admins can see this: Facts you" : "Admin view (hidden from other members): Facts this member"} confirmed before approved sources backed them up`} data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800">
      <Eye size={12} aria-hidden />Early confirmer · {n}</span>
  );
};

export const TopContributorBadge = ({ user, testid = "top-contributor-badge" }) => {
  const { me } = useMe();
  const p = privateStats(user, me);
  const list = p?.c?.top_contributor || [];
  if (!list.length) return null;
  const b = list[0];
  const month = new Date(`${b.month}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <span title={`${p.own ? "Only you and Middle's admins can see this: you were" : "Admin view (hidden from other members): this member was"} #${b.rank} contributor in ${month}${list.length > 1 ? ` (top three ${list.length} times)` : ""}`} data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
      <Trophy size={12} aria-hidden />Top contributor · {month}{list.length > 1 && <span className="border-l border-amber-200 pl-1" data-testid={`${testid}-count`}>×{list.length}</span>}</span>
  );
};

export const ReportButton = ({ targetType, targetId, trigger }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("Inappropriate");
  const [details, setDetails] = useState("");
  const reasons = ["Not true", "Hate speech", "Political opinion", "Propaganda", "Inappropriate", "Harassment or hate", "Spam or scam", "Prohibited item", "Other"];
  const submit = async () => {
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason, details });
      toast.success("Thanks — our moderators will review this.");
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button variant="ghost" size="sm" data-testid={`report-${targetType}-btn`}><Flag size={14} className="mr-1.5" />Report</Button>}</DialogTrigger>
      <DialogContent data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Report this {targetType}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {reasons.map((r) => <button key={r} data-testid={`report-reason-${r.split(" ")[0].toLowerCase()}`} onClick={() => setReason(r)} className={`chip ${reason === r ? "chip-active" : ""}`}>{r}</button>)}
        </div>
        <Textarea data-testid="report-details-input" placeholder="Anything else we should know? (optional)" value={details} onChange={(e) => setDetails(e.target.value)} />
        <Button data-testid="report-submit-btn" onClick={submit}>Submit report</Button>
      </DialogContent>
    </Dialog>
  );
};

export const Spinner = () => <div data-testid="loading" className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;

// A clickable card that opens `to`, without being an <a> (so it can hold links and inputs inside).
export const CardLink = ({ to, children, ...rest }) => {
  const nav = useNavigate();
  const go = (e) => { if (!e.defaultPrevented && !window.getSelection()?.toString()) nav(to); };
  return <div role="link" tabIndex={0} onClick={go} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && nav(to)} {...rest}>{children}</div>;
};
